import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { format, isToday } from "date-fns";
import { 
  Calendar, Award, X, Camera, Check, Loader2, Ban, MapPin
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Calendar as CalendarComponent } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useBooking, PACKAGES, ADDONS } from "../../context";
import { useAuth } from "../../context";
import { API, TIME_SLOTS } from "../../lib/constants";

// Address Autocomplete Component using Backend API
const AddressAutocomplete = ({ value, onChange, placeholder, className, testId }) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const fetchSuggestions = useCallback(async (input) => {
    if (!input || input.length < 3) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/places/autocomplete`, {
        params: { input }
      });
      
      if (response.data.predictions) {
        setSuggestions(response.data.predictions);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    setInputValue(val);
    onChange(val);
    setShowSuggestions(true);

    // Debounce the API call
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(val);
    }, 300);
  };

  const handleSelect = (description) => {
    setInputValue(description);
    onChange(description);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div className="relative">
      <Input
        value={inputValue}
        onChange={handleInput}
        onFocus={() => setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 250)}
        placeholder={placeholder}
        className={className}
        data-testid={testId}
        autoComplete="off"
      />
      {showSuggestions && suggestions.length > 0 && (
        <ul className="absolute z-[99999] w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-60 overflow-auto">
          {suggestions.map((suggestion) => (
            <li
              key={suggestion.place_id}
              onMouseDown={() => handleSelect(suggestion.description)}
              className="px-4 py-3 hover:bg-slate-700 cursor-pointer text-sm border-b border-slate-700 last:border-0 flex items-center gap-2"
            >
              <MapPin className="w-4 h-4 text-cyan-400 flex-shrink-0" />
              <span className="text-slate-200">{suggestion.description}</span>
            </li>
          ))}
        </ul>
      )}
      {isLoading && inputValue.length >= 3 && (
        <div className="absolute z-[99999] w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg p-3 text-sm text-slate-400 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading suggestions...
        </div>
      )}
    </div>
  );
};

const BookingModal = () => {
  const { 
    showBookingModal, setShowBookingModal,
    vehicleType, setVehicleType,
    selectedPackage, setSelectedPackage,
    addons, setAddons,
    discountCode, setDiscountCode
  } = useBooking();

  const [step, setStep] = useState(1);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [agreeToCancellation, setAgreeToCancellation] = useState(false);
  const [carPhotos, setCarPhotos] = useState([]);
  const [carAtHome, setCarAtHome] = useState(true);
  const [referralCode, setReferralCode] = useState("");
  const [referralValid, setReferralValid] = useState(null);
  const [createAccount, setCreateAccount] = useState(false);
  const [accountPassword, setAccountPassword] = useState("");
  const [discountValidation, setDiscountValidation] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [blockedSlotsForDate, setBlockedSlotsForDate] = useState([]);
  const [loadingBlockedSlots, setLoadingBlockedSlots] = useState(false);
  const [fullyBlockedDates, setFullyBlockedDates] = useState([]);
  const fileInputRef = useRef(null);
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const { user, register } = useAuth();
  const [birthdayDiscount, setBirthdayDiscount] = useState(null);
  const [travelFee, setTravelFee] = useState(0);
  const [loadingTravelFee, setLoadingTravelFee] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_name: "",
    phone: "",
    email: "",
    address: "",
    car_address: "",
    make_model: "",
    car_condition: "",
    special_instructions: "",
    preferred_time: "",
    preferred_contact: "text"
  });

  // Filter out past times if selected date is today
  const times = useMemo(() => {
    if (!date || !isToday(date)) {
      return TIME_SLOTS;
    }
    
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    return TIME_SLOTS.filter(timeSlot => {
      // Parse time slot (e.g., "8:00 AM", "12:30 PM")
      const [time, period] = timeSlot.split(' ');
      const [hours, minutes] = time.split(':').map(Number);
      
      // Convert to 24-hour format
      let hour24 = hours;
      if (period === 'PM' && hours !== 12) {
        hour24 = hours + 12;
      } else if (period === 'AM' && hours === 12) {
        hour24 = 0;
      }
      
      // Compare with current time (add 30 min buffer for booking lead time)
      const slotMinutes = hour24 * 60 + minutes;
      const currentMinutes = currentHour * 60 + currentMinute + 30; // 30 min buffer
      
      return slotMinutes > currentMinutes;
    });
  }, [date]);

  // Fetch all fully blocked dates when modal opens
  useEffect(() => {
    const fetchFullyBlockedDates = async () => {
      if (!showBookingModal) return;
      try {
        const response = await axios.get(`${API}/blocked-dates-full`);
        setFullyBlockedDates(response.data.blocked_dates || []);
      } catch (error) {
        console.error("Failed to fetch blocked dates:", error);
        setFullyBlockedDates([]);
      }
    };
    fetchFullyBlockedDates();
  }, [showBookingModal]);

  // Fetch blocked slots when date changes
  useEffect(() => {
    const fetchBlockedSlots = async () => {
      if (!date) {
        setBlockedSlotsForDate([]);
        return;
      }
      setLoadingBlockedSlots(true);
      try {
        const dateStr = format(date, "yyyy-MM-dd");
        const response = await axios.get(`${API}/blocked-slots/${dateStr}`);
        setBlockedSlotsForDate(response.data.blocked_slots || []);
      } catch (error) {
        console.error("Failed to fetch blocked slots:", error);
        setBlockedSlotsForDate([]);
      } finally {
        setLoadingBlockedSlots(false);
      }
    };
    fetchBlockedSlots();
    
    // Clear selected time if it's no longer available (past time on today)
    if (date && isToday(date) && formData.preferred_time) {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      
      const [time, period] = formData.preferred_time.split(' ');
      const [hours, minutes] = time.split(':').map(Number);
      
      let hour24 = hours;
      if (period === 'PM' && hours !== 12) {
        hour24 = hours + 12;
      } else if (period === 'AM' && hours === 12) {
        hour24 = 0;
      }
      
      const slotMinutes = hour24 * 60 + minutes;
      const currentMinutes = currentHour * 60 + currentMinute + 30;
      
      if (slotMinutes <= currentMinutes) {
        setFormData(prev => ({ ...prev, preferred_time: "" }));
      }
    }
  }, [date]);

  // Check for birthday discount
  useEffect(() => {
    const checkBirthdayDiscount = async () => {
      if (user?.id) {
        try {
          const response = await axios.get(`${API}/check-birthday-discount/${user.id}`);
          if (response.data.eligible) {
            setBirthdayDiscount(response.data);
          }
        } catch (error) {
          console.error("Failed to check birthday discount:", error);
        }
      }
    };
    checkBirthdayDiscount();
  }, [user?.id]);

  // Calculate travel fee when address changes
  useEffect(() => {
    const fetchTravelFee = async () => {
      const address = carAtHome ? formData.address : formData.car_address;
      if (!address || address.length < 5) {
        setTravelFee(0);
        return;
      }
      
      // Extract suburb from address (usually after the street name, before state/postcode)
      // Format: "123 Street, Suburb VIC 3000" or "Suburb, VIC"
      const parts = address.split(',');
      let suburb = '';
      if (parts.length >= 2) {
        // Get the second last part which usually contains suburb
        const suburbPart = parts[parts.length - 2]?.trim() || parts[0]?.trim();
        // Remove numbers (postcodes, street numbers)
        suburb = suburbPart.replace(/\d+/g, '').trim().split(' ')[0];
      } else {
        suburb = address.split(' ')[0];
      }
      
      if (!suburb) {
        setTravelFee(0);
        return;
      }
      
      setLoadingTravelFee(true);
      try {
        const response = await axios.post(`${API}/calculate-travel-fee`, { suburb });
        setTravelFee(response.data.travel_fee || 0);
      } catch (error) {
        console.error("Failed to calculate travel fee:", error);
        setTravelFee(0);
      } finally {
        setLoadingTravelFee(false);
      }
    };
    
    // Debounce the travel fee calculation
    const timeoutId = setTimeout(fetchTravelFee, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.address, formData.car_address, carAtHome]);

  // Pre-fill form with user data if logged in
  useEffect(() => {
    if (user && showBookingModal) {
      setFormData(prev => ({
        ...prev,
        customer_name: prev.customer_name || user.name || "",
        phone: prev.phone || user.phone || "",
        email: prev.email || user.email || "",
        address: prev.address || user.home_address || ""
      }));
    }
  }, [user, showBookingModal]);

  // Find next available date
  const findNextAvailableDate = async () => {
    setLoadingBlockedSlots(true);
    try {
      const response = await axios.get(`${API}/next-available-date`);
      if (response.data.date) {
        const nextDate = new Date(response.data.date);
        setDate(nextDate);
        toast.success(`Next available date: ${format(nextDate, "PPP")}`);
      } else {
        toast.info("No blocked dates found - all dates are available!");
      }
    } catch (error) {
      toast.error("Failed to find next available date");
    } finally {
      setLoadingBlockedSlots(false);
    }
  };

  // Validate discount code via API
  const validateDiscountCode = async () => {
    if (!discountCode.trim()) {
      setDiscountValidation(null);
      return;
    }
    
    setValidatingDiscount(true);
    try {
      const response = await axios.post(`${API}/validate-discount?code=${encodeURIComponent(discountCode)}`);
      setDiscountValidation(response.data);
      if (response.data.valid) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      setDiscountValidation({ valid: false, message: "Failed to validate code" });
      toast.error("Failed to validate discount code");
    } finally {
      setValidatingDiscount(false);
    }
  };

  const handleDiscountCodeChange = (value) => {
    setDiscountCode(value);
    setDiscountValidation(null);
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + carPhotos.length > 5) {
      toast.error("Maximum 5 photos allowed");
      return;
    }
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setCarPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (index) => {
    setCarPhotos(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const calculateTotal = () => {
    if (!selectedPackage) return { package: 0, addons: 0, travelFee: 0, total: 0, discount: 0, discountType: null };
    const pkgPrice = PACKAGES[selectedPackage]?.basePrices[vehicleType] || 0;
    const addonsPrice = addons.reduce((sum, id) => {
      const addon = ADDONS.find(a => a.id === id);
      return sum + (addon?.price || 0);
    }, 0);
    let total = pkgPrice + addonsPrice + travelFee;
    let discountAmount = 0;
    let discountType = null;
    
    // Birthday discount takes priority (50% off during birthday month)
    if (birthdayDiscount?.eligible && !discountCode) {
      discountAmount = total * 0.5;
      total = total * 0.5;
      discountType = "50% Birthday Reward";
    }
    // Apply validated discount from API
    else if (discountValidation?.valid && discountValidation.discount) {
      const disc = discountValidation.discount;
      if (disc.type === "percent") {
        discountAmount = total * (disc.value / 100);
        total = total - discountAmount;
        discountType = `${disc.value}% discount code`;
      } else if (disc.type === "fixed") {
        discountAmount = Math.min(disc.value, total);
        total = Math.max(0, total - disc.value);
        discountType = `$${disc.value} off code`;
      }
    }
    // Fallback for loyalty codes
    else if (discountCode.toUpperCase() === "LOYAL50" && user?.loyalty_points >= 5) {
      discountAmount = total * 0.5;
      total *= 0.5;
      discountType = "50% Loyalty Reward";
    } else if (discountCode.toUpperCase() === "FREESERVICE" && user?.free_services_earned > 0) {
      discountAmount = total;
      total = 0;
      discountType = "Free Service Reward";
    }
    // Apply referral discount if user has it available and no other discount
    else if (user?.referral_discount_available && !discountCode) {
      discountAmount = total * 0.15;
      total *= 0.85;
      discountType = "15% Referral Reward";
    }
    
    return { package: pkgPrice, addons: addonsPrice, travelFee, total, discount: discountAmount, discountType };
  };

  const validateReferralCode = async (code) => {
    if (!code || code.length < 6) {
      setReferralValid(null);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/validate-referral/${code}`);
      setReferralValid(response.data);
    } catch (error) {
      setReferralValid({ valid: false });
    }
  };

  const toggleAddon = (addonId) => {
    setAddons(prev => 
      prev.includes(addonId) 
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  const handleSubmit = async () => {
    if (!formData.customer_name || !formData.phone || !formData.address || !date || !formData.preferred_time) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (!carAtHome && !formData.car_address) {
      toast.error("Please enter the car location address");
      return;
    }

    if (!agreeToTerms || !agreeToCancellation) {
      toast.error("Please agree to all terms and policies to continue");
      return;
    }

    // Email is always required
    if (!formData.email) {
      toast.error("Email address is required");
      return;
    }

    if (createAccount) {
      if (!accountPassword || accountPassword.length < 6) {
        toast.error("Password must be at least 6 characters");
        return;
      }
    }

    setLoading(true);
    try {
      let newUserId = null;
      if (createAccount && formData.email && accountPassword) {
        try {
          const regResponse = await register(
            formData.customer_name,
            formData.email,
            accountPassword,
            formData.phone
          );
          newUserId = regResponse.user?.id;
          toast.success("Account created! Check your email to verify.");
        } catch (regError) {
          if (regError.response?.status === 400) {
            toast.error(regError.response?.data?.detail || "Email already registered. Please login instead.");
            setLoading(false);
            return;
          }
          console.error("Registration error:", regError);
        }
      }

      const response = await axios.post(`${API}/bookings`, {
        ...formData,
        car_at_home: carAtHome,
        car_address: carAtHome ? null : formData.car_address,
        vehicle_type: vehicleType,
        package: selectedPackage,
        addons,
        discount_code: discountCode,
        referral_code: referralValid?.valid ? referralCode : null,
        preferred_date: format(date, "yyyy-MM-dd"),
        origin_url: window.location.origin,
        has_photos: carPhotos.length > 0,
        user_id: newUserId || user?.id
      });

      const bookingId = response.data.booking?.id;

      if (carPhotos.length > 0 && bookingId) {
        const photoFormData = new FormData();
        photoFormData.append('booking_id', bookingId);
        carPhotos.forEach(photo => {
          photoFormData.append('files', photo.file);
        });
        try {
          await axios.post(`${API}/upload-photos`, photoFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (photoError) {
          console.error("Photo upload error:", photoError);
        }
      }

      // Handle booking response - new flow without Stripe redirect
      if (response.data.status === "pending" || response.data.booking_id) {
        toast.success("Booking request submitted! We'll contact you to confirm your time and send payment details.");
        setShowBookingModal(false);
        resetModal();
        setLoading(false);
      } else {
        console.error("Unexpected response:", response.data);
        toast.error("Something went wrong. Please try again.");
        setLoading(false);
      }
    } catch (error) {
      console.error("Booking error:", error);
      toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const resetModal = () => {
    setStep(1);
    setAgreeToTerms(false);
    setAgreeToCancellation(false);
    setCarPhotos([]);
    setCarAtHome(true);
    setReferralCode("");
    setReferralValid(null);
    setFormData({
      customer_name: user?.name || "",
      phone: user?.phone || "",
      email: user?.email || "",
      address: user?.home_address || "",
      car_address: "",
      make_model: "",
      car_condition: "",
      special_instructions: "",
      preferred_time: "",
      preferred_contact: "text"
    });
    setDate(null);
    setDiscountValidation(null);
    setBirthdayDiscount(null);
    setCreateAccount(false);
    setAccountPassword("");
  };

  useEffect(() => {
    if (!showBookingModal) {
      resetModal();
    }
  }, [showBookingModal]);

  const prices = calculateTotal();

  return (
    <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            Book Your Detail
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Step {step} of 3
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Step 1: Package & Vehicle */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <Label className="text-sm font-medium mb-3 block">Select Vehicle Type</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: "small", label: "Small" },
                    { id: "medium", label: "SUV/Med" },
                    { id: "large", label: "Large/Van" },
                    { id: "xl", label: "XL/Truck" }
                  ].map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setVehicleType(v.id)}
                      className={`py-3 px-4 rounded-lg border transition ${
                        vehicleType === v.id 
                          ? "border-cyan-400 bg-cyan-400/10 text-cyan-400" 
                          : "border-slate-700 text-slate-400 hover:border-slate-600"
                      }`}
                      data-testid={`modal-vehicle-${v.id}`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium mb-3 block">Select Package</Label>
                <div className="space-y-3">
                  {Object.entries(PACKAGES).map(([key, pkg]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedPackage(key)}
                      className={`w-full p-4 rounded-lg border text-left transition ${
                        selectedPackage === key 
                          ? "border-cyan-400 bg-cyan-400/10" 
                          : "border-slate-700 hover:border-slate-600"
                      }`}
                      data-testid={`modal-package-${key}`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-medium">{pkg.name}</span>
                          {pkg.popular && (
                            <span className="ml-2 text-xs bg-cyan-400 text-slate-900 px-2 py-0.5 rounded-full">
                              Popular
                            </span>
                          )}
                        </div>
                        <span className="text-cyan-400 font-bold">${pkg.basePrices[vehicleType]}</span>
                      </div>
                      <p className="text-sm text-slate-400 mt-1">{pkg.tagline}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium mb-3 block">Optional Add-ons</Label>
                <div className="grid grid-cols-2 gap-2">
                  {ADDONS.map((addon) => (
                    <button
                      key={addon.id}
                      onClick={() => toggleAddon(addon.id)}
                      className={`p-3 rounded-lg border text-left text-sm transition ${
                        addons.includes(addon.id) 
                          ? "border-cyan-400 bg-cyan-400/10" 
                          : "border-slate-700 hover:border-slate-600"
                      }`}
                      data-testid={`addon-${addon.id}`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-slate-300">{addon.name}</span>
                        <span className="text-cyan-400">+${addon.price}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Date & Time */}
          {step === 2 && (
            <div className="space-y-6">
              {/* Birthday Discount Banner */}
              {birthdayDiscount?.eligible && (
                <div className="bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30 rounded-xl p-4 animate-pulse-slow">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">🎂</div>
                    <div>
                      <p className="text-pink-400 font-bold">Happy Birthday!</p>
                      <p className="text-sm text-slate-300">Enjoy <span className="text-pink-400 font-bold">50% OFF</span> your service this month!</p>
                      <p className="text-xs text-slate-400">Valid until {birthdayDiscount.valid_until}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <Label className="text-sm font-medium mb-3 block">Preferred Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left bg-slate-800 border-slate-700 hover:bg-slate-700"
                      data-testid="date-picker-trigger"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {date ? format(date, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-slate-800 border-slate-700">
                    <CalendarComponent
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (d < today) return true;
                        // Check if date is fully blocked
                        const dateStr = format(d, "yyyy-MM-dd");
                        return fullyBlockedDates.some(b => b.date === dateStr);
                      }}
                      modifiers={{
                        blocked: (d) => {
                          const dateStr = format(d, "yyyy-MM-dd");
                          return fullyBlockedDates.some(b => b.date === dateStr);
                        }
                      }}
                      modifiersClassNames={{
                        blocked: "line-through text-red-400 opacity-50"
                      }}
                      className="bg-slate-800"
                      data-testid="calendar"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-medium">Preferred Time</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={findNextAvailableDate}
                    disabled={loadingBlockedSlots}
                    className="text-xs bg-cyan-600/20 border-cyan-500/30 hover:bg-cyan-600/30 text-cyan-400"
                  >
                    {loadingBlockedSlots ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Calendar className="w-3 h-3 mr-1" />
                    )}
                    Next Available Date
                  </Button>
                </div>
                
                {/* Show blocked slots warning */}
                {date && blockedSlotsForDate.length > 0 && (
                  <div className="mb-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <Ban className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-amber-400 text-sm font-medium">Some times unavailable on this date</p>
                        {blockedSlotsForDate.some(s => s.time === null) ? (
                          <p className="text-amber-300/80 text-xs mt-1">
                            This entire day is blocked{blockedSlotsForDate[0]?.reason ? `: ${blockedSlotsForDate[0].reason}` : ""}
                          </p>
                        ) : (
                          <p className="text-amber-300/80 text-xs mt-1">
                            Some time slots are unavailable
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Show message when today has limited times */}
                {date && isToday(date) && times.length < TIME_SLOTS.length && times.length > 0 && (
                  <div className="mb-3 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                    <p className="text-blue-400 text-sm">
                      Showing available times for today. Earlier slots have passed.
                    </p>
                  </div>
                )}
                
                {/* Show message when no times available today */}
                {date && isToday(date) && times.length === 0 && (
                  <div className="mb-3 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    <p className="text-red-400 text-sm">
                      No more time slots available today. Please select another date.
                    </p>
                  </div>
                )}
                
                <Select 
                  value={formData.preferred_time} 
                  onValueChange={(v) => setFormData({...formData, preferred_time: v})}
                  disabled={(date && blockedSlotsForDate.some(s => s.time === null)) || (date && isToday(date) && times.length === 0)}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="time-select">
                    <SelectValue placeholder={
                      date && blockedSlotsForDate.some(s => s.time === null) 
                        ? "Day fully blocked" 
                        : date && isToday(date) && times.length === 0
                        ? "No times available today"
                        : "Select time"
                    } />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                    {times.map((time) => {
                      const isBlocked = blockedSlotsForDate.some(s => s.time === time);
                      const blockReason = blockedSlotsForDate.find(s => s.time === time)?.reason;
                      return (
                        <SelectItem 
                          key={time} 
                          value={time}
                          disabled={isBlocked}
                          className={isBlocked ? "text-slate-500 line-through" : ""}
                        >
                          {time}
                          {isBlocked && blockReason && ` (${blockReason})`}
                          {isBlocked && !blockReason && " (Unavailable)"}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Time Request Note */}
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                <p className="text-sm text-slate-300">
                  <span className="text-cyan-400 font-medium">Note:</span> This is a time request only. We will contact you via your preferred method to confirm the finalised time and cost.
                </p>
                
                <div className="mt-3">
                  <Label className="text-sm font-medium mb-2 block">Preferred Contact Method</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="preferred_contact"
                        value="text"
                        checked={formData.preferred_contact === "text"}
                        onChange={(e) => setFormData({...formData, preferred_contact: e.target.value})}
                        className="w-4 h-4 text-cyan-400 bg-slate-800 border-slate-600"
                        data-testid="contact-text-radio"
                      />
                      <span className="text-slate-300">Text (SMS)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="preferred_contact"
                        value="email"
                        checked={formData.preferred_contact === "email"}
                        onChange={(e) => setFormData({...formData, preferred_contact: e.target.value})}
                        className="w-4 h-4 text-cyan-400 bg-slate-800 border-slate-600"
                        data-testid="contact-email-radio"
                      />
                      <span className="text-slate-300">Email</span>
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Discount Code (optional)</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter code"
                    value={discountCode}
                    onChange={(e) => handleDiscountCodeChange(e.target.value)}
                    className={`bg-slate-800 border-slate-700 flex-1 ${
                      discountValidation?.valid ? 'border-green-500' : 
                      discountValidation && !discountValidation.valid ? 'border-red-500' : ''
                    }`}
                    data-testid="discount-code-input"
                  />
                  <Button
                    type="button"
                    onClick={validateDiscountCode}
                    disabled={!discountCode.trim() || validatingDiscount}
                    className="bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 disabled:text-slate-500"
                    data-testid="apply-discount-btn"
                  >
                    {validatingDiscount ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Apply"
                    )}
                  </Button>
                </div>
                {discountValidation?.valid && (
                  <p className="text-green-400 text-xs mt-1 flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    {discountValidation.message}
                  </p>
                )}
                {discountValidation && !discountValidation.valid && (
                  <p className="text-red-400 text-xs mt-1">{discountValidation.message}</p>
                )}
              </div>

              {/* Available Rewards Section */}
              {user && (
                <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/30 rounded-lg p-4">
                  <p className="text-sm font-medium text-cyan-400 mb-2 flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    Your Available Rewards
                  </p>
                  {user.loyalty_points >= 5 && user.loyalty_points < 10 ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-300">50% Off Available!</p>
                        <p className="text-xs text-slate-500">Enter code: <span className="text-cyan-400 font-mono">LOYAL50</span></p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setDiscountCode('LOYAL50')}
                        className="text-xs bg-cyan-600 hover:bg-cyan-500 px-3 py-1 rounded"
                      >
                        Apply
                      </button>
                    </div>
                  ) : user.free_services_earned > 0 ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-yellow-400">FREE Service Available!</p>
                        <p className="text-xs text-slate-500">Enter code: <span className="text-yellow-400 font-mono">FREESERVICE</span></p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setDiscountCode('FREESERVICE')}
                        className="text-xs bg-yellow-600 hover:bg-yellow-500 px-3 py-1 rounded"
                      >
                        Apply
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">
                      {5 - (user.loyalty_points || 0)} more bookings until 50% off! Keep going!
                    </p>
                  )}
                </div>
              )}

              <div>
                <Label className="text-sm font-medium mb-2 block">Referral Code (optional)</Label>
                <Input
                  placeholder="Got a friend's code?"
                  value={referralCode}
                  onChange={(e) => {
                    setReferralCode(e.target.value.toUpperCase());
                    validateReferralCode(e.target.value);
                  }}
                  className={`bg-slate-800 border-slate-700 ${referralValid?.valid ? 'border-green-500' : ''}`}
                  data-testid="referral-code-input"
                />
                {referralValid?.valid && (
                  <p className="text-green-400 text-xs mt-1">
                    Referred by {referralValid.referrer_name}
                  </p>
                )}
                {referralValid && !referralValid.valid && referralCode.length >= 6 && (
                  <p className="text-red-400 text-xs mt-1">Invalid referral code</p>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Contact Details */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">Full Name *</Label>
                <Input
                  placeholder="Your name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                  data-testid="customer-name-input"
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Phone *</Label>
                <Input
                  placeholder="0400 000 000"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                  data-testid="phone-input"
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Email *</Label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                  data-testid="email-input"
                  required
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Home Address *</Label>
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(val) => setFormData({...formData, address: val})}
                  placeholder="Start typing your address..."
                  className="bg-slate-800 border-slate-700"
                  testId="address-input"
                />
              </div>

              {/* Car Location */}
              <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="car-at-home" 
                    checked={carAtHome}
                    onCheckedChange={setCarAtHome}
                    data-testid="car-at-home-checkbox"
                  />
                  <label htmlFor="car-at-home" className="text-sm text-slate-300 cursor-pointer">
                    Car is at my home address
                  </label>
                </div>
                
                {!carAtHome && (
                  <div>
                    <Label className="text-sm font-medium mb-2 block text-cyan-400">Car Location Address *</Label>
                    <Input
                      placeholder="Where is the car located?"
                      value={formData.car_address}
                      onChange={(e) => setFormData({...formData, car_address: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                      data-testid="car-address-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Travel fee will be calculated from this location
                    </p>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Vehicle Make & Model</Label>
                <Input
                  placeholder="e.g. Toyota Camry 2020"
                  value={formData.make_model}
                  onChange={(e) => setFormData({...formData, make_model: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                  data-testid="make-model-input"
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Car Condition</Label>
                <Select 
                  value={formData.car_condition} 
                  onValueChange={(v) => setFormData({...formData, car_condition: v})}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="condition-select">
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="light">Light - Regular maintenance</SelectItem>
                    <SelectItem value="moderate">Moderate - Some dirt/stains</SelectItem>
                    <SelectItem value="heavy">Heavy - Significant cleaning needed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Special Instructions (optional)</Label>
                <Textarea
                  placeholder="Any specific areas to focus on? Pet hair? Etc."
                  value={formData.special_instructions}
                  onChange={(e) => setFormData({...formData, special_instructions: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                  rows={3}
                  data-testid="instructions-textarea"
                />
              </div>

              {/* Photo Upload Section */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Upload Car Photos (optional)</Label>
                <p className="text-xs text-slate-500 mb-3">Upload up to 5 photos for more accurate quotation</p>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  multiple
                  className="hidden"
                  data-testid="photo-input"
                />
                <div className="flex flex-wrap gap-2 mb-3">
                  {carPhotos.map((photo, index) => (
                    <div key={index} className="relative w-20 h-20 rounded-lg overflow-hidden bg-slate-800">
                      <img src={photo.preview} alt={`Car ${index + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {carPhotos.length < 5 && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-600 flex flex-col items-center justify-center text-slate-500 hover:border-cyan-400 hover:text-cyan-400 transition"
                    >
                      <Camera className="w-5 h-5 mb-1" />
                      <span className="text-xs">Add</span>
                    </button>
                  )}
                </div>
                {carPhotos.length === 0 && (
                  <p className="text-xs text-yellow-400 bg-yellow-400/10 p-2 rounded-lg">
                    <span className="font-medium">Note:</span> If no photos are uploaded, final cost may change depending on the condition of your car.
                  </p>
                )}
              </div>

              {/* Account Creation Option */}
              {!user && formData.email && (
                <div className="bg-gradient-to-r from-cyan-400/10 to-teal-500/10 border border-cyan-400/30 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Checkbox 
                      id="create-account" 
                      checked={createAccount}
                      onCheckedChange={setCreateAccount}
                      data-testid="create-account-checkbox"
                    />
                    <div className="flex-1">
                      <label htmlFor="create-account" className="text-sm font-medium text-cyan-400 cursor-pointer block mb-1">
                        Create an account with these details
                      </label>
                      <p className="text-xs text-slate-400">
                        Earn loyalty rewards (5 services = 50% OFF, 10 = FREE!), manage bookings easily, and get exclusive offers
                      </p>
                    </div>
                  </div>
                  
                  {createAccount && (
                    <div className="mt-3 ml-7">
                      <Label className="text-xs text-slate-400 mb-1 block">Create a password</Label>
                      <Input
                        type="password"
                        placeholder="Choose a password (min 6 characters)"
                        value={accountPassword}
                        onChange={(e) => setAccountPassword(e.target.value)}
                        className="bg-slate-800 border-slate-700"
                        data-testid="account-password-input"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Guest Checkout Benefits Reminder */}
              {!user && !createAccount && formData.email && (
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-400">
                    <span className="text-cyan-400 font-medium">Booking as guest?</span> You'll miss out on loyalty rewards (FREE services!) and easier booking management.{' '}
                    <button 
                      type="button"
                      onClick={() => setCreateAccount(true)}
                      className="text-cyan-400 underline hover:text-cyan-300"
                    >
                      Create account instead
                    </button>
                  </p>
                </div>
              )}

              {/* Summary */}
              <div className="bg-slate-800 rounded-lg p-4 mt-6">
                <h4 className="font-medium mb-3">Order Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">{PACKAGES[selectedPackage]?.name}</span>
                    <span>${prices.package}</span>
                  </div>
                  {prices.addons > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Add-ons</span>
                      <span>${prices.addons}</span>
                    </div>
                  )}
                  {prices.travelFee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Travel Fee</span>
                      <span>${prices.travelFee.toFixed(2)}</span>
                    </div>
                  )}
                  {prices.travelFee === 0 && formData.address && (
                    <div className="flex justify-between text-green-400">
                      <span>Travel Fee</span>
                      <span>FREE</span>
                    </div>
                  )}
                  {prices.discount > 0 && (
                    <div className="flex justify-between text-green-400">
                      <span>{prices.discountType}</span>
                      <span>-${prices.discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-slate-700 pt-2 flex justify-between font-bold">
                    <span>Total</span>
                    <span className="text-cyan-400">${prices.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Terms Agreement */}
              <div className="space-y-3 mt-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms-agreement"
                    checked={agreeToTerms}
                    onCheckedChange={setAgreeToTerms}
                    className="mt-0.5 border-slate-600 data-[state=checked]:bg-cyan-400 data-[state=checked]:border-cyan-400"
                    data-testid="terms-checkbox"
                  />
                  <label 
                    htmlFor="terms-agreement" 
                    className="text-sm text-slate-400 cursor-pointer"
                  >
                    I have read and agree to the <a href="/terms" onClick={(e) => { e.preventDefault(); window.open('/terms', '_blank'); }} className="text-cyan-400 hover:underline">terms & conditions of booking</a> *
                  </label>
                </div>
                
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="cancellation-agreement"
                    checked={agreeToCancellation}
                    onCheckedChange={setAgreeToCancellation}
                    className="mt-0.5 border-slate-600 data-[state=checked]:bg-cyan-400 data-[state=checked]:border-cyan-400"
                    data-testid="cancellation-checkbox"
                  />
                  <label 
                    htmlFor="cancellation-agreement" 
                    className="text-sm text-slate-400 cursor-pointer"
                  >
                    I have read and agree to the <a href="/terms" onClick={(e) => { e.preventDefault(); window.open('/terms', '_blank'); }} className="text-cyan-400 hover:underline">cancellation policies</a> *
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-3 pt-4">
            {step > 1 && (
              <Button 
                variant="outline" 
                onClick={() => setStep(step - 1)}
                className="flex-1 border-slate-700 hover:bg-slate-800"
                data-testid="back-btn"
              >
                Back
              </Button>
            )}
            {step < 3 ? (
              <Button 
                onClick={() => setStep(step + 1)}
                disabled={step === 1 && !selectedPackage}
                className="flex-1 btn-primary-glow text-slate-900"
                data-testid="next-btn"
              >
                Next
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 btn-primary-glow text-slate-900"
                data-testid="submit-booking-btn"
              >
                {loading ? "Processing..." : "Request Booking"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
